import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import type { CompanyScopeOption } from '@/utils/companyAutocompleteScope';
import { getModulePermissions } from '@/__core/permissions/config/modulePermissions';
import {
  hasGlobalRoleScope,
  PERMISSION_ACTIONS,
  PERMISSION_RESOURCES,
  type CompanyPermission,
  type Role,
} from './PermissionManager';
import { roleHasPermission } from './permissionResolver';

/** Map module toolbar keys → permission resource for list/read scope */
export const MODULE_LIST_READ_RESOURCE: Record<string, string> = {
  users: PERMISSION_RESOURCES.USERS,
  roles: PERMISSION_RESOURCES.ROLES,
  employees: PERMISSION_RESOURCES.EMPLOYEES,
  'fixed-salary': PERMISSION_RESOURCES.EMPLOYEES_FIXED_SALARY,
  insurance: PERMISSION_RESOURCES.EMPLOYEES_INSURANCE,
  companies: PERMISSION_RESOURCES.COMPANIES,
  customers: PERMISSION_RESOURCES.CUSTOMERS,
  suppliers: PERMISSION_RESOURCES.SUPPLIERS,
  orders: PERMISSION_RESOURCES.ORDERS,
  'order-progress': 'orders.orderprogress',
  transactions: PERMISSION_RESOURCES.TRANSACTION,
  'financial-report': PERMISSION_RESOURCES.TRANSACTION,
  departments: PERMISSION_RESOURCES.DEPARTMENTS,
  allocations: PERMISSION_RESOURCES.INVENTORY_MATERIAL_REQUEST,
  purchase: 'inventory.purchase',
  'request-material': 'inventory.materialrequest',
  items: PERMISSION_RESOURCES.INVENTORY_ITEM,
  stockbalance: 'inventory.stockbalance',
  stocktransactions: 'inventory.stocktransaction',
  stores: 'inventory.store',
  receipts: 'inventory.receipt',
  returns: 'inventory.return',
  issues: 'inventory.issue',
  stocktakes: 'inventory.stocktake',
  timekeepings: 'timekeepings',
  timesheets: 'timekeepings',
};

export type ModuleCompanyScopeKey = keyof typeof MODULE_LIST_READ_RESOURCE | string;

export interface ResolveModuleAccessibleCompaniesInput {
  moduleKey: ModuleCompanyScopeKey;
  resource?: string;
  readAction?: string;
  companies: CompanyData[];
  globalRoles: Role[];
  selectedCompany?: CompanyPermission | null;
  hasGlobalRole?: boolean;
  allCompaniesFallback?: CompanyScopeOption[];
}

export interface ModuleAccessibleCompaniesResult {
  shouldShowAutocomplete: boolean;
  hasGlobalRole: boolean;
  mode: 'global' | 'company' | 'hidden';
  companies: CompanyScopeOption[];
  defaultCompanyId: string | null;
}

const normalizeId = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const toScopeOption = (company: CompanyData): CompanyScopeOption | null => {
  const companyId = normalizeId(company.company_id);
  if (!companyId) return null;
  return {
    company_id: companyId,
    company_name: normalizeId(company.company_name) || companyId,
    company_code: normalizeId(company.company_code),
    company_type: normalizeId(company.company_type) || 'company',
  };
};

const toScopeOptionFromPartial = (
  company: Partial<CompanyScopeOption>
): CompanyScopeOption | null => {
  const companyId = normalizeId(company.company_id);
  if (!companyId) return null;
  return {
    company_id: companyId,
    company_name: normalizeId(company.company_name) || companyId,
    company_code: normalizeId(company.company_code),
    company_type: normalizeId(company.company_type) || 'company',
  };
};

const dedupeCompanies = (companies: CompanyScopeOption[]): CompanyScopeOption[] => {
  const map = new Map<string, CompanyScopeOption>();
  companies.forEach((company) => {
    const id = company.company_id;
    if (!id) return;
    const existing = map.get(id);
    map.set(id, existing ? { ...existing, ...company, company_name: existing.company_name || company.company_name } : company);
  });
  return Array.from(map.values());
};

const prioritizeDefaultCompany = (
  companies: CompanyScopeOption[],
  defaultCompanyId: string | null
): CompanyScopeOption[] => {
  if (!defaultCompanyId) return companies;
  const index = companies.findIndex((c) => c.company_id === defaultCompanyId);
  if (index <= 0) return companies;
  const next = [...companies];
  const [item] = next.splice(index, 1);
  next.unshift(item);
  return next;
};

const getModuleReadChecks = (
  moduleKey: string,
  resource: string,
  readAction: string
): Array<{ resource: string; action: string }> => {
  const fromConfig = getModulePermissions(moduleKey);
  if (fromConfig.length > 0) return fromConfig;
  return [{ resource, action: readAction }];
};

export const roleHasResourceAction = roleHasPermission;

export const companyHasResourceRead = (
  company: CompanyData,
  resource: string,
  action: string = PERMISSION_ACTIONS.READ
): boolean => {
  const roles = company.roles || [];
  return roles.some((role) => roleHasPermission(role, resource, action));
};

export const companyHasModuleListAccess = (
  company: CompanyData,
  moduleKey: string,
  resource: string,
  readAction: string = PERMISSION_ACTIONS.READ
): boolean => {
  const roles = company.roles || [];
  if (roles.length === 0) return false;

  const checks = getModuleReadChecks(moduleKey, resource, readAction);
  return checks.some(({ resource: checkResource, action: checkAction }) =>
    roles.some((role) => roleHasPermission(role, checkResource, checkAction))
  );
};

const mapAuthCompanies = (companies: CompanyData[]): CompanyScopeOption[] =>
  dedupeCompanies(
    companies
      .map(toScopeOption)
      .filter((c): c is CompanyScopeOption => c !== null)
  );

const resolveDefaultCompanyId = (
  selectedCompany?: CompanyPermission | null
): string | null => {
  const id = normalizeId(selectedCompany?.company_id);
  if (!id || id === 'global') return null;
  return id;
};

/**
 * Companies the user may filter lists by for a module: global role → all accessible companies;
 * otherwise only companies where the user has module read on at least one role.
 */
export function resolveModuleAccessibleCompanies(
  input: ResolveModuleAccessibleCompaniesInput
): ModuleAccessibleCompaniesResult {
  const resource =
    input.resource ||
    MODULE_LIST_READ_RESOURCE[input.moduleKey] ||
    input.moduleKey;
  const readAction = input.readAction || PERMISSION_ACTIONS.READ;
  const authCompanies = Array.isArray(input.companies) ? input.companies : [];
  const globalRoles = Array.isArray(input.globalRoles) ? input.globalRoles : [];
  const hasGlobalRole = hasGlobalRoleScope(
    globalRoles,
    input.selectedCompany,
    input.hasGlobalRole
  );
  const defaultCompanyId = resolveDefaultCompanyId(input.selectedCompany);

  if (hasGlobalRole) {
    let candidates = mapAuthCompanies(authCompanies);
    const fallback = (input.allCompaniesFallback || [])
      .map(toScopeOptionFromPartial)
      .filter((c): c is CompanyScopeOption => c !== null);

    if (candidates.length === 0 && fallback.length > 0) {
      candidates = dedupeCompanies(fallback);
    }

    const companies = prioritizeDefaultCompany(candidates, defaultCompanyId);

    return {
      shouldShowAutocomplete: true,
      hasGlobalRole: true,
      mode: 'global',
      companies,
      defaultCompanyId,
    };
  }

  const companiesWithRoles = authCompanies.filter((company) => (company.roles || []).length > 0);

  const filtered = companiesWithRoles.filter((company) =>
    companyHasModuleListAccess(company, input.moduleKey, resource, readAction)
  );

  if (
    process.env.NODE_ENV === 'development' &&
    filtered.length === 0 &&
    companiesWithRoles.length > 0
  ) {
    console.warn(
      `[resolveModuleAccessibleCompanies] No company has module access for "${input.moduleKey}" (resource checks: ${resource}).`
    );
  }

  const companies = prioritizeDefaultCompany(mapAuthCompanies(filtered), defaultCompanyId);

  return {
    shouldShowAutocomplete: companies.length > 1,
    hasGlobalRole: false,
    mode: companies.length > 0 ? 'company' : 'hidden',
    companies,
    defaultCompanyId,
  };
}
